import React from 'react'
import { Choose } from 'react-extras'

import { Col, DatePicker, Form, Input, InputNumber, LocaleProvider, TimePicker } from 'antd'
import MaskedInput from 'antd-mask-input'
import ptBR from 'antd/es/locale/pt_BR'
import moment from 'moment'
import 'moment/locale/pt-br'

import { getMessage } from '../../util/IntlMessages'
import CustomLabel from '../CustomLabel/CustomLabel'

const { RangePicker, MonthPicker } = DatePicker
moment.updateLocale(moment.locale('pt-br'), { invalidDate: '-' })

function changeInput(setFieldValue, field, event, onChangeWithField, customFormId, type) {
  const eventValue = type === 'number' ? event : event.target.value
  setFieldValue(field, eventValue, customFormId)
  if (onChangeWithField) {
    onChangeWithField(field, eventValue, customFormId)
  }
}

function renderFormItem(label, field, children, errors = {}) {
  return (
    <Form.Item label={label} validateStatus={errors[field] ? 'error' : 'validating'} help={errors[field]}>
      {children}
    </Form.Item>
  )
}

function validValidDateTime(value) {
  if (value !== null && value !== undefined && value !== '' && moment(value).isValid()) {
    return moment(value)
  }

  return null
}

function renderDatePicker(label, props, type) {
  const { dateFormat = type === 'month' ? 'MM/YYYY' : 'DD/MM/YYYY' } = props

  const dateFormatMapper = {
    'DD/MM/AAAA': 'DD/MM/YYYY',
    'MM/DD/AAAA': 'MM/DD/YYYY',
    'AAAA/MM/DD': 'YYYY/MM/DD'
  }

  const { name = '' } = props
  const renderFieldName = name ? name : props.field
  const textDatePicker = (
    <LocaleProvider locale={ptBR}>
      <Choose>
        <Choose.When condition={type === 'dateRange'}>
          <RangePicker
            disabled={props.disabled || false}
            style={{ width: '100%' }}
            ranges={
              (validValidDateTime(props.value[renderFieldName] && props.value[renderFieldName][0]),
              validValidDateTime(props.value[renderFieldName] && props.value[renderFieldName][1]))
            }
            format={dateFormatMapper[dateFormat] || dateFormat}
            suffixIcon={null}
            placeholder={props.placeholder}
            onChange={(date) => {
              props.setFieldValue(renderFieldName, date)
            }}
            id={props.id}
          />
        </Choose.When>
        <Choose.When condition={type === 'month'}>
          <MonthPicker
            disabled={props.disabled || false}
            style={{ width: '100%' }}
            value={validValidDateTime(props.value[renderFieldName])}
            format={dateFormatMapper[dateFormat] || dateFormat}
            suffixIcon={null}
            placeholder={props.placeholder}
            onChange={(date) => {
              props.setFieldValue(renderFieldName, date)
            }}
            id={props.id}
          />
        </Choose.When>
        <Choose.Otherwise>
          <DatePicker
            disabled={props.disabled || false}
            style={{ width: '100%' }}
            value={validValidDateTime(props.value[renderFieldName])}
            format={dateFormatMapper[dateFormat] || dateFormat}
            suffixIcon={null}
            placeholder={props.placeholder}
            onChange={(date) => {
              props.setFieldValue(renderFieldName, date)
            }}
            id={props.id}
          />
        </Choose.Otherwise>
      </Choose>
    </LocaleProvider>
  )

  return renderFormItem(label, renderFieldName, textDatePicker, props.errors)
}

function renderTimePicker(label, props) {
  const { name = '' } = props
  const renderFieldName = name ? name : props.field

  const textTimePicker = (
    <LocaleProvider locale={ptBR}>
      <TimePicker
        format="HH:mm"
        disabled={props.disabled || false}
        id={props.id}
        style={{ width: '100%' }}
        value={validValidDateTime(props.value[renderFieldName])}
        suffixIcon={null}
        onChange={(date, dateString) => {
          console.tron.log('onChange', date, dateString)
          props.setFieldValue(renderFieldName, date)
        }}
      />
    </LocaleProvider>
  )
  return renderFormItem(label, renderFieldName, textTimePicker, props.errors)
}

function renderTextArea(label, props) {
  const { name = '', customFormId = '' } = props
  const renderFieldName = name ? name : props.field

  const textArea = (
    <Input.TextArea
      rows={5}
      id={props.id}
      prefix={props.prefix}
      placeholder={props.placeholder}
      type={props.type}
      name={props.value[renderFieldName]}
      value={props.value[renderFieldName]}
      onBlur={props.handleBlur}
      required={props.required}
      disabled={props.disabled || false}
      allowClear
      onChange={(event) =>
        changeInput(props.setFieldValue, renderFieldName, event, props.onChangeWithField, customFormId, props.type)
      }
    />
  )
  return renderFormItem(label, renderFieldName, textArea, props.errors)
}

function renderInputNumber(label, props) {
  const { name = '', customFormId = '' } = props
  const renderFieldName = name ? name : props.field

  const inputNumber = (
    <InputNumber
      id={props.id}
      value={props.value[renderFieldName]}
      size="middle"
      style={{ width: '100%' }}
      name={props.value[renderFieldName]}
      step={props.step}
      defaultValue={props.defaultValue}
      onBlur={props.handleBlur}
      disabled={props.disabled || false}
      onChange={(event) =>
        changeInput(props.setFieldValue, renderFieldName, event, props.onChangeWithField, customFormId, props.type)
      }
      min={0}
    />
  )
  return renderFormItem(label, renderFieldName, inputNumber, props.errors)
}

/**
 * @description The function is designed to return a specified
 * Component based on a set of conditions based on the input props.
 * By default it'll return a spread of the props indiscriminately,
 * but that can be modified to easier filter or remove attributes.
 */
const getInputCompProps = (props) =>
  (() => {
    function ret(InputComponent, default_props) {
      // Determines what generic props are allowed into the Input components.
      // Those can still be rewritten by the default_props parameter.
      const { type, disabled, prefix, placeholder, max, min, mask, visibilitytoggle, required, onKeyPress } = props
      return {
        InputComponent,
        inputProps: {
          ...default_props,
          type,
          disabled,
          prefix,
          required,
          placeholder,
          max,
          min,
          mask,
          visibilitytoggle,
          onKeyPress
        }
      }
    }

    switch (true) {
      // MASKED INPUT
      case props.mask != null && props.mask !== '':
        return new ret(MaskedInput)
      // PASSWORD INPUT
      case props.type === 'password':
        return new ret(Input.Password)
      // REGULAR INPUT
      default:
        return new ret(Input)
    }
  })()

function renderInputText(label, props) {
  const { name = '', customFormId = '', className = '', id = '' } = props
  const renderFieldName = name ? name : props.field

  const { InputComponent, inputProps } = getInputCompProps(props)
  const textInput = (
    <InputComponent
      {...inputProps}
      name={renderFieldName}
      value={props.value[renderFieldName]}
      onBlur={props.handleBlur}
      required={props.required}
      className={className}
      id={id}
      allowClear
      onChange={
        props.handleChange ||
        ((event) =>
          changeInput(props.setFieldValue, renderFieldName, event, props.onChangeWithField, customFormId, props.type))
      }
    />
  )

  return renderFormItem(label, renderFieldName, textInput, props.errors)
}

function getLabelName({ customNameField, field, suffixLabel, customFormId }) {
  let strFieldText = field
  // Caso nao tenha customFormId é campo fixo e deve estar no language a traducao
  if (!customFormId || customFormId === '') {
    strFieldText = getMessage(field)
  }
  return `${customNameField ? getMessage(customNameField) : strFieldText}${suffixLabel ? ' '.concat(suffixLabel) : ''}`
}

const CustomInput = (props) => {
  const { rowSpan, type, textArea, labelInput, colStyle } = props
  const label = !labelInput ? CustomLabel(getLabelName(props), props) : labelInput

  return (
    <Col xs={24} sm={24} md={rowSpan} lg={rowSpan} xl={rowSpan} style={colStyle}>
      <Choose>
        <Choose.When condition={type === 'textarea' && textArea !== null}>{renderTextArea(label, props)}</Choose.When>
        <Choose.When condition={type === 'number'}>{renderInputNumber(label, props)}</Choose.When>
        <Choose.Otherwise>
          <Choose>
            <Choose.When condition={type === 'date' || type === 'dateRange' || type === 'month'}>
              {renderDatePicker(label, props, type)}
            </Choose.When>
            <Choose.When condition={type === 'hour'}>{renderTimePicker(label, props)}</Choose.When>
            <Choose.Otherwise>{renderInputText(label, props)}</Choose.Otherwise>
          </Choose>
        </Choose.Otherwise>
      </Choose>
    </Col>
  )
}

export default React.memo(CustomInput)
